import ConfirmationDialog from '@/components/ui/ConfirmationDialog'
import { OwnersContext, OwnersContextType } from '@/contexts/OwnersContext'
import { useOwners } from '@/hooks/useOwners'
import { OwnerFormData } from '@/schemas/ownerSchema'
import { BaseFilters, OwnerWithPropertyCount } from '@/types'
import { useDisclosure } from '@chakra-ui/react'
import React, { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { OwnerFormRHF } from '../OwnerFormRHF'

interface OwnersProviderProps {
  children: ReactNode
}

export const OwnersProvider: React.FC<OwnersProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<BaseFilters>({
    search: '',
    sortBy: 'name',
    page: 1,
    limit: 12,
    showDeleted: false,
  })
  const [selectedOwner, setSelectedOwner] =
    useState<OwnerWithPropertyCount | null>(null)
  const [ownerToDelete, setOwnerToDelete] =
    useState<OwnerWithPropertyCount | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure()

  // Get CRUD operations from useOwners hook
  const {
    data,
    count,
    totalPages,
    currentPage,
    statistics,
    stats,
    isLoading,
    error,
    createOwner,
    updateOwner,
    softDelete,
    sendVerification,
    isCreating,
    isUpdating,
    isSendingVerification,
  } = useOwners(filters)

  // Handlers
  const handleFormOpen = (owner?: OwnerWithPropertyCount) => {
    if (owner) {
      setSelectedOwner(owner)
    } else {
      setSelectedOwner(null)
    }
    setIsFormOpen(true)
  }

  const handleFormSubmit = (formData: OwnerFormData) => {
    if (selectedOwner) {
      updateOwner(
        {
          id: selectedOwner.id,
          data: {
            ...formData,
            phone: formData.phone || undefined,
          },
        },
        {
          onSuccess: () => {
            setIsFormOpen(false)
            setSelectedOwner(null)
          },
        }
      )
    } else {
      createOwner(
        {
          ...formData,
          phone: formData.phone || undefined,
        },
        {
          onSuccess: () => {
            setIsFormOpen(false)
          },
        }
      )
    }
  }

  const handleFormClose = (isEditing: boolean = false) => {
    setIsFormOpen(false)
    if (!isEditing) {
      setSelectedOwner(null)
    }
  }

  const handleSendVerification = (email: string) => {
    sendVerification(email)
  }

  const handleEdit = (owner: OwnerWithPropertyCount) => {
    setFilters((prev) => ({ ...prev, id: owner.id }))
    setIsFormOpen(true)
  }

  const handleDelete = (userId: string) => {
    // Find the owner to delete for the dialog
    const owner = Array.isArray(data) ? data.find((o) => o.id === userId) : data
    if (owner) {
      setOwnerToDelete(owner)
      onDeleteDialogOpen()
    }
  }

  const handleConfirmDelete = () => {
    if (ownerToDelete) {
      softDelete(ownerToDelete.id)
    }
  }

  const handleCloseDeleteDialog = () => {
    onDeleteDialogClose()
    setOwnerToDelete(null)
  }

  const handleRowClick = (owner: OwnerWithPropertyCount) => {
    navigate(`/owners/${owner.id}`)
  }
  const value: OwnersContextType = {
    // State
    filters,
    data,
    totalPages,
    currentPage,
    isLoading,
    isSendingVerification,
    isCreating,
    isUpdating,
    error,
    count: count || 0,
    statistics,
    stats,
    isFormOpen,
    selectedOwner,
    isDeleteDialogOpen,
    ownerToDelete,

    // Actions
    setFilters,
    handleFormOpen,
    handleRowClick,
    handleSendVerification,
    handleEdit,
    handleDelete,
    handleFormSubmit,
    handleFormClose,
    onDeleteDialogOpen,
    onDeleteDialogClose,
    handleCloseDeleteDialog,
    handleConfirmDelete,
    createOwner,
    updateOwner,
    softDelete,
    // restore,
  }

  return (
    <OwnersContext.Provider value={value}>
      {children}
      {/* Owner Form Modal */}
      <OwnerFormRHF
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        owner={selectedOwner}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      {ownerToDelete && (
        <ConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir o proprietário "${ownerToDelete.name}"?`}
          confirmText="Excluir"
          confirmColorScheme="red"
        />
      )}
    </OwnersContext.Provider>
  )
}
